import { DomainChange } from '~/domain/changes/DomainChange';
import { DomainStore } from '~/domain/changes/DomainStore';
import { redactDicomUids } from '~/utils/phiRedaction';

export interface PersistenceService {
    dispose(): void;
}

/**
 * Strip / mask PHI from a {@link DomainChange} so the changeset can be
 * logged without leaking patient-identifying data to the console (or
 * any console-capture integration). Per CLAUDE.md and the SaMD
 * privacy posture: never log PHI in plaintext.
 *
 * Two-stage redaction:
 *   1. `patient` snapshots: dicomPatientName and dicomPatientId are
 *      explicit identifiers — replace with `[redacted]` so the field
 *      is visibly gone (not a UID-shaped substring that might fool a
 *      regex-based filter downstream).
 *   2. Every snapshot: DICOM UID-shaped substrings (sopInstanceUid,
 *      seriesInstanceUid, frameOfReferenceUid, etc.) are masked via
 *      {@link redactDicomUids}. UIDs are PHI under common HIPAA
 *      readings because they can re-identify a patient via cross-
 *      reference with the PACS / planning system.
 */
function redactPhi(change: DomainChange): DomainChange {
    const withFieldsMasked: DomainChange =
        change.kind === 'patient'
            ? {
                  ...change,
                  snapshot: {
                      ...change.snapshot,
                      dicomPatientId: '[redacted]',
                      dicomPatientName: '[redacted]',
                  },
              }
            : change;
    return redactDicomUids(withFieldsMasked);
}

export class LoggingPersistenceService implements PersistenceService {
    private unsubscribe: () => void;

    constructor(store: DomainStore) {
        this.unsubscribe = store.onChangeset((changes) => {
            // console.debug for production filterability; PHI redacted
            // up-front so this log line is always safe to emit.
            // eslint-disable-next-line no-console
            console.debug('[domain-persistence]', changes.map(redactPhi));
        });
    }

    dispose(): void {
        this.unsubscribe();
    }
}
